import { useState, useEffect, useCallback } from 'react';
import { getShootingPreferences, type UserShootingPreferences } from '@/services/data/shootingPreferences';

interface ShootingPreferencesStatus {
  hasInstallationType: boolean;
  hasDecorColor: boolean;
  hasThumbnailStyle: boolean;
  hasDecorComplete: boolean; // true if both decor color AND thumbnail are set
  preferences: UserShootingPreferences | null;
  isLoading: boolean;
  refetch: () => void;
}

export function useShootingPreferencesStatus(userId: string | undefined): ShootingPreferencesStatus {
  const [preferences, setPreferences] = useState<UserShootingPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const checkPreferences = useCallback(async () => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const result = await getShootingPreferences(userId);
      
      if (result.success && result.data) {
        setPreferences(result.data);
      } else {
        setPreferences(null);
      }
    } catch (error) {
      console.error('Error checking shooting preferences:', error);
      setPreferences(null);
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    checkPreferences();
  }, [checkPreferences]);

  const refetch = useCallback(() => {
    checkPreferences();
  }, [checkPreferences]);

  const hasDecorColor = Boolean(preferences?.decor_color);
  const hasThumbnailStyle = Boolean(preferences?.thumbnail_style);

  return {
    hasInstallationType: Boolean(preferences?.installation_type),
    hasDecorColor,
    hasThumbnailStyle,
    hasDecorComplete: hasDecorColor && hasThumbnailStyle, // Both must be set
    preferences,
    isLoading,
    refetch,
  };
}
